import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import LiveVideo from "../components/LiveVideo";
import ChatPanel from "../components/ChatPanel";
import LiveReactions from "../components/LiveReactions";
import MilestoneMeter, { MilestoneHitPopup, playCoinSting, vibrateMilestone } from "../components/MilestoneMeter";
import Countdown from "../components/Countdown";
import SoldFlashOverlay from "../components/SoldFlashOverlay";
import PostDropSurvey from "../components/PostDropSurvey";
import PreLive from "./PreLive";
import { addAllToGoogleCalendar } from "../lib/calendar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { useTrackDropView } from "../lib/dropEngagement";
import LikeButton from "../components/LikeButton";

/**
 * Minimal Live Room.
 * - During the 2-min live: video + viewer count + current milestone + stock + chat only.
 * - After the timer ends (final_window): a clean Buy card appears with the locked price.
 * Admins are redirected to the dedicated broadcast console.
 */
export default function LiveRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, admin } = useAuth();
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [defaultAddr, setDefaultAddr] = useState(null);
  const [addrChecked, setAddrChecked] = useState(false);
  // Ephemeral milestone-unlock popup state — replaced on each new hit so the
  // key changes and the CSS pop animation replays. Dedupe by tier index
  // because we mount TWO meters (mobile + desktop DOM layers) and each fires
  // the callback independently on the same crossing.
  const [milestonePop, setMilestonePop] = useState(null);
  const lastPopIdxRef = useRef(-1);
  const onMilestoneHit = useCallback((tier, index) => {
    if (index <= lastPopIdxRef.current) return; // already announced
    lastPopIdxRef.current = index;
    setMilestonePop({ tier, index, key: Date.now() });
    // Sensory nudge — 200ms coin-drop sound + light haptic. Fired at the
    // parent level so we don't double-fire from the mobile+desktop popup mounts.
    playCoinSting();
    vibrateMilestone();
    try { track("milestone_hit_client", { live_id: id, tier: index + 1, price: tier.price }); } catch (_e) { /* noop */ }
  }, [id]);

  const join = useCallback(async () => {
    try { const r = await api.post(`/lives/${id}/join`); setLive(r.data); track("live_join", { live_id: id }); }
    catch (e) { console.error(e); }
  }, [id]);

  // Track this page view — dedupe key = user_id | anon session — so the
  // cohost console can gauge pre-drop demand over the 24h before scheduled.
  useTrackDropView(id);

  const consecutiveFailsRef = useRef(0);
  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/lives/${id}`);
      setLive(r.data);
      consecutiveFailsRef.current = 0;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Live not found");
        nav("/");
        return;
      }
      // Transient blip — don't kick the viewer out mid-drop. Retry silently.
      consecutiveFailsRef.current += 1;
      if (consecutiveFailsRef.current >= 5) {
        toast.error("Lost connection to the drop. Returning home.");
        nav("/");
      }
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => {
    if (admin) return;
    refresh().then(() => join());
    const iv = setInterval(refresh, 3000);
    return () => clearInterval(iv);
  }, [refresh, join, admin]);

  useEffect(() => {
    if (!user) { setDefaultAddr(null); setAddrChecked(true); return; }
    api.get("/addresses").then((r) => {
      const list = r.data || [];
      setDefaultAddr(list.find((a) => a.is_default) || list[0] || null);
      setAddrChecked(true);
    }).catch(() => setAddrChecked(true));
  }, [user]);

  if (admin) return <Navigate to={`/admin/live/${id}`} replace />;

  const book = async (qty = 1) => {
    if (!user) { toast.error("Please sign in to book"); nav("/login"); return; }
    if (!defaultAddr) {
      toast.error("Add a shipping address first", {
        action: { label: "Add", onClick: () => nav("/profile") },
        duration: 5000,
      });
      return;
    }
    if (!window.Razorpay) { toast.error("Payment SDK didn't load. Refresh the page."); return; }
    setBooking(true);
    try {
      const r = await api.post(`/bookings`, { live_id: id, quantity: qty });
      const bkg = r.data;
      track("booking_created", { live_id: id, booking_id: bkg.booking_id, total: bkg.total });
      const options = {
        key: bkg.razorpay_key_id,
        amount: bkg.razorpay_amount,
        currency: "INR",
        name: "Drop Shop Live",
        description: live.title || "Live drop purchase",
        order_id: bkg.razorpay_order_id,
        prefill: { name: user.name || "", email: user.email || "", contact: defaultAddr.phone || "" },
        notes: { booking_id: bkg.booking_id, ships_to: `${defaultAddr.city} ${defaultAddr.postal_code}` },
        theme: { color: "#FF6A00" },
        handler: async (resp) => {
          try {
            await api.post(`/payments/verify`, {
              booking_id: bkg.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            track("payment_success", { live_id: id, booking_id: bkg.booking_id, total: bkg.total });
            toast.success(`Paid ₹${bkg.total}! Order ${bkg.booking_id} — shipping to ${defaultAddr.city}.`, { duration: 6000 });
            refresh();
          } catch (err) { toast.error(err.response?.data?.detail || "Payment verification failed"); }
        },
        modal: {
          ondismiss: async () => {
            const stopAt = Date.now() + 15000;
            let confirmed = false;
            while (Date.now() < stopAt) {
              try {
                const list = await api.get("/bookings/me");
                const b = (list.data || []).find((x) => x.booking_id === bkg.booking_id);
                if (b && b.status === "confirmed") { confirmed = true; toast.success(`Paid ₹${b.total}! Order ${b.booking_id}`, { duration: 6000 }); break; }
                if (b && (b.status === "payment_failed" || b.status === "cancelled")) break;
              } catch (e) { console.warn("post-modal poll failed:", e); }
              await new Promise((r) => setTimeout(r, 2000));
            }
            if (!confirmed) {
              try { await api.post(`/payments/cancel?booking_id=${bkg.booking_id}`); } catch (e) { console.warn("payment cancel failed:", e); }
              toast("Checkout closed. If you completed payment, it'll appear in Orders shortly.", { duration: 4500 });
            }
            refresh();
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => toast.error("Payment failed. Stock is held — try again."));
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
      refresh();
    } finally { setBooking(false); }
  };

  if (loading || !live) {
    return <div className="min-h-screen flex items-center justify-center font-mono-ds text-[#6E6E6E]">// Loading drop…</div>;
  }

  const status = live.status;
  const isLive = status === "live";
  const isFinalWindow = status === "final_window";
  const isEnded = status === "ended";
  const isScheduled = status === "scheduled";
  const isOpen = status === "open";
  // Within the 5-minute pre-open window? Chat + waiting-room video slot open,
  // even if the host hasn't hit Start yet. Members hang out with the host live.
  const scheduledMs = live.scheduled_at ? new Date(live.scheduled_at).getTime() : null;
  const preopenActive = isScheduled
    && scheduledMs !== null
    && (scheduledMs - Date.now()) <= 5 * 60 * 1000
    && (scheduledMs - Date.now()) > -30 * 60 * 1000;
  const isWaitingRoom = isOpen || preopenActive;
  const canBuy = isFinalWindow;               // Buy only opens AFTER the 2-min live ends
  const stock = live.product?.stock ?? 0;
  const milestone = currentMilestone(live);

  // Pre-live experience: rich waiting-room page when the drop is still >5 min
  // out. Once we cross into the pre-open window (or the host opens the room),
  // fall through to the full LiveRoom so members are IN the same room as the
  // host — video slot + chat overlay — before the 2-min timer starts.
  if (isScheduled && !preopenActive) {
    return <PreLive live={live} memberNo={user?.member_no} isAnonymous={!user && !admin} onAddToCalendar={() => addAllToGoogleCalendar("prelive_page")} />;
  }

  // Anonymous visitors arriving after the drop has gone live / ended see a
  // gentle sign-in gate instead of the full room. They cannot buy without an
  // account, and streaming a live feed to anonymous eyeballs is not the
  // Founding-Members experience we're preserving.
  if (!user && !admin && (isLive || isFinalWindow || isEnded)) {
    return <AnonymousLiveGate live={live} status={status} />;
  }

  return (
    <>
      {/* ========================= MOBILE ========================= */}
      <div className="lg:hidden fixed inset-0 bg-[#0F0F0F] text-white overflow-hidden" style={{ height: "100dvh" }} data-testid="live-room-mobile">
        {/* Video / state layer */}
        <div className="absolute inset-0">
          {(isLive || isFinalWindow) ? (
            <LiveVideo channel={live.channel} mode="audience" autoJoin />
          ) : isWaitingRoom ? (
            <PlaceholderScreen tag="● Waiting Room" title="Live is about to start" sub="Host is setting up — the 2-min drop begins shortly" />
          ) : (
            <PlaceholderScreen tag="Archive" title="Drop Ended" sub={`${live.participants} attended`} />
          )}
        </div>

        {/* Top gradient for legibility. Bottom gradient removed — the whitish
            fade was fighting the video and eating vertical space. */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />

        {/* Top bar: Back · LIVE · Timer · Viewers */}
        <div className="absolute top-0 inset-x-0 z-20 flex items-center gap-2 px-3" style={{ paddingTop: "max(10px, env(safe-area-inset-top))" }}>
          <Link to="/" className="w-9 h-9 flex items-center justify-center border border-white/20 bg-black/50 backdrop-blur text-white/80 hover:text-white" data-testid="back-link" aria-label="Back">←</Link>
          {(isLive || isFinalWindow) && <LivePill isFinalWindow={isFinalWindow} />}
          {isLive && live.live_ends_at && <TimerChip to={live.live_ends_at} label="LIVE" onComplete={refresh} />}
          {isFinalWindow && live.final_window_ends_at && <TimerChip to={live.final_window_ends_at} label="BUY" accent onComplete={refresh} />}
          <div className="ml-auto flex items-center gap-2">
            <LikeButton liveId={live.live_id} overlay />
            <ViewerChip n={live.participants} />
          </div>
        </div>

        {/* Reactions dock (mobile) — 👍 👎 ❤️ 🔥 with floating glyphs.
            Push above the milestone meter (~82px) + chat input (~52px) + gap
            so the 🔥 button never lands on the SEND button. */}
        {(isLive || isFinalWindow) && (
          <LiveReactions
            liveId={live.live_id}
            memberNo={user?.member_no}
            position="bottom-right"
            compact
            bottomPx={canBuy ? 300 : 168}
          />
        )}

        {/* Bottom: chat overlay full-width above buy CTA */}
        {(isLive || isFinalWindow || isWaitingRoom) && (
          <div className="absolute z-20 inset-x-0" style={{ bottom: canBuy ? "calc(env(safe-area-inset-bottom) + 132px + 82px)" : "calc(env(safe-area-inset-bottom) + 82px)" }}>
            <ChatPanel liveId={live.live_id} liveStatus={live.status} userName={user?.name || "Guest"} isHost={false} overlay onMilestonesUpdated={refresh} />
          </div>
        )}

        {/* Milestone meter — neon lime bar, orange fill. Sits right below the
            chat "Type a question…" input on the mobile live view. */}
        {(isLive || isFinalWindow) && (
          <div className="absolute inset-x-0 z-20" style={{ bottom: canBuy ? "calc(env(safe-area-inset-bottom) + 132px)" : "calc(env(safe-area-inset-bottom) + 0px)" }}>
            <MilestoneMeter
              milestones={live.milestones || []}
              participants={live.participants || 0}
              onMilestoneHit={onMilestoneHit}
              compact
            />
          </div>
        )}

        {/* Milestone-unlocked centre popup — plays once, unmounts on animation done */}
        {milestonePop && (isLive || isFinalWindow) && (
          <MilestoneHitPopup
            key={milestonePop.key}
            tier={milestonePop.tier}
            index={milestonePop.index}
            onDone={() => setMilestonePop(null)}
          />
        )}

        {/* Buy CTA — ONLY in final_window (after the 2-min timer) */}
        {canBuy && (
          <div className="absolute inset-x-0 bottom-0 z-30 bg-[#0F0F0F]/95 backdrop-blur border-t border-white/10" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <div className="px-3 pt-3 pb-3">
              {user && addrChecked && !defaultAddr && (
                <button onClick={() => nav("/profile")} className="w-full mb-2 border border-dashed border-white/25 hover:border-[#FF6A00] hover:text-[#FF6A00] px-3 py-1.5 font-mono-ds text-[10px] uppercase tracking-widest text-white/70 transition-colors flex items-center justify-between" data-testid="ship-chip-empty">
                  <span>+ Add shipping address to buy</span><span className="text-[#FF6A00]">→</span>
                </button>
              )}
              <Button onClick={() => book(1)} disabled={booking || stock <= 0} className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FF7B1A] text-[#161616] font-mono-ds uppercase tracking-widest h-12 font-bold disabled:bg-white/20 disabled:text-white/50 disabled:cursor-not-allowed" data-testid="buy-now-btn">
                {stock <= 0 ? "Sold Out" : booking ? "Processing…" : `The Final Price ₹${live.current_price} →`}
              </Button>
              {/* Scrollable info strip — title · stock · milestone tiers. Kept
                  below the buy button so the live viewport stays uncluttered. */}
              <LiveInfoStrip live={live} milestone={milestone} stock={stock} />
            </div>
          </div>
        )}
        {isEnded && (
          <div className="absolute inset-x-0 bottom-0 z-30 bg-black/85 backdrop-blur border-t border-white/10 px-3 py-3" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <Link to="/home" className="w-full h-11 border border-white/25 hover:border-[#FF6A00] hover:text-[#FF6A00] flex items-center justify-center font-mono-ds text-[11px] uppercase tracking-widest text-white/70" data-testid="next-drops-link">See Next Drops →</Link>
          </div>
        )}

        <SoldFlashOverlay liveId={live.live_id} />
      </div>

      {/* ========================= DESKTOP ========================= */}
      <div className="hidden lg:block min-h-screen bg-[#F8F7F2] text-[#161616]">
        {/* Slim header */}
        <div className="px-6 md:px-10 py-4 border-b border-[#E5E1D8] flex items-center justify-between" data-testid="live-room-desktop">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/" className="font-mono-ds text-xs uppercase tracking-[0.3em] text-[#6E6E6E] hover:text-[#FF6A00]" data-testid="back-link-desktop">← Drops</Link>
            <div className="w-px h-6 bg-[#E5E1D8]" />
            <div className="font-display text-xl uppercase tracking-tight truncate" data-testid="live-title">{live.title}</div>
          </div>
          <div className="flex items-center gap-3">
            {(isLive || isFinalWindow) && <LivePill isFinalWindow={isFinalWindow} light />}
            {isLive && live.live_ends_at && <TimerChip to={live.live_ends_at} label="LIVE ENDS" light onComplete={refresh} />}
            {isFinalWindow && live.final_window_ends_at && <TimerChip to={live.final_window_ends_at} label="BUY CLOSES" accent light onComplete={refresh} />}
            <LikeButton liveId={live.live_id} />
            <ViewerChip n={live.participants} light />
          </div>
        </div>

        {/* 2-column: video + chat */}
        <div className="grid grid-cols-12 gap-3 px-3 py-3">
          {/* Video column */}
          <div className="col-span-9 flex flex-col gap-3">
            <div className="relative aspect-video bg-black" data-testid="live-video-wrap">
              {(isLive || isFinalWindow) ? (
                <LiveVideo channel={live.channel} mode="audience" autoJoin />
              ) : isWaitingRoom ? (
                <PlaceholderScreen tag="● Waiting Room" title="Live is about to start" sub="Host is setting up — the 2-min drop begins shortly" light />
              ) : (
                <PlaceholderScreen tag="Archive" title="Drop Ended" sub={`${live.participants} attended`} light />
              )}

              {/* Milestone unlocked popup — center of the video */}
              {milestonePop && (isLive || isFinalWindow) && (
                <MilestoneHitPopup
                  key={milestonePop.key}
                  tier={milestonePop.tier}
                  index={milestonePop.index}
                  onDone={() => setMilestonePop(null)}
                />
              )}
            </div>

            {/* Reactions dock (desktop) — inside the video column, bottom-right */}
            {(isLive || isFinalWindow) && (
              <LiveReactions liveId={live.live_id} memberNo={user?.member_no} position="bottom-right" />
            )}

            {/* Post-live buy card — ONLY in final_window */}
            {canBuy && (
              <div className="border border-[#E5E1D8] bg-[#FFFFFF] shadow-[4px_4px_0_#264733] p-6 flex items-center gap-6" data-testid="buy-card-desktop">
                {live.product?.image_url && (
                  <img src={live.product.image_url} alt={live.product.name} className="w-24 h-24 object-cover border border-[#E5E1D8] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="brand-tag">// Buy Window Open · Locked Price</div>
                  <div className="font-display text-2xl uppercase tracking-tight mt-1 truncate" data-testid="buy-card-product">{live.product?.name}</div>
                  <div className="flex items-baseline gap-3 mt-1">
                    <span className="font-mono-ds text-3xl font-bold text-[#FF6A00]" data-testid="live-price">₹{live.current_price}</span>
                    {live.product?.base_price > live.current_price && (
                      <span className="font-mono-ds text-sm text-[#6E6E6E] line-through">₹{live.product.base_price}</span>
                    )}
                    <span className="ml-auto"><StockChip stock={stock} /></span>
                  </div>
                </div>
                <div className="w-64 flex flex-col gap-2">
                  {user && addrChecked && !defaultAddr && (
                    <button onClick={() => nav("/profile")} className="w-full border border-dashed border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] px-3 py-2 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] flex items-center justify-between" data-testid="ship-chip-empty-desktop">
                      <span>+ Add shipping address</span><span className="text-[#FF6A00]">→</span>
                    </button>
                  )}
                  {defaultAddr && (
                    <div className="border border-[#E5E1D8] bg-[#F8F7F2] px-3 py-2 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] truncate" data-testid="ship-chip">
                      Ships to {defaultAddr.city} · {defaultAddr.postal_code}
                    </div>
                  )}
                  <Button onClick={() => book(1)} disabled={booking || stock <= 0} className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FF7B1A] text-[#161616] font-mono-ds uppercase tracking-widest h-12 font-bold disabled:bg-[#E5E1D8] disabled:text-[#6E6E6E] disabled:cursor-not-allowed" data-testid="buy-now-btn-desktop">
                    {stock <= 0 ? "Sold Out" : booking ? "Processing…" : `The Final Price ₹${live.current_price} →`}
                  </Button>
                </div>
              </div>
            )}
            {isEnded && (
              <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-5 flex items-center justify-between" data-testid="ended-strip">
                <div>
                  <div className="brand-tag">// Drop Ended</div>
                  <div className="font-display text-lg uppercase tracking-tight mt-1">Thanks for tuning in.</div>
                </div>
                <Link to="/home" className="border border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] px-4 h-11 flex items-center font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E]" data-testid="next-drops-link-desktop">See Next Drops →</Link>
              </div>
            )}
          </div>

          {/* Chat column */}
          <div className="col-span-3 min-h-[520px] flex flex-col gap-2" data-testid="chat-column">
            <div className="flex-1 min-h-[520px] font-mono-ds text-[12px]">
              <ChatPanel liveId={live.live_id} liveStatus={live.status} userName={user?.name || "Guest"} isHost={false} onMilestonesUpdated={refresh} />
            </div>
            {/* Milestone meter under the chat input on desktop */}
            {(isLive || isFinalWindow) && (
              <MilestoneMeter
                milestones={live.milestones || []}
                participants={live.participants || 0}
                onMilestoneHit={onMilestoneHit}
              />
            )}
          </div>
        </div>
        <SoldFlashOverlay liveId={live.live_id} />
      </div>

      {/* Post-drop survey — rendered once at fragment root */}
      {isEnded && <PostDropSurvey liveId={live.live_id} liveTitle={live.title} />}
    </>
  );
}

/* ------------------------------ helpers ------------------------------ */
function currentMilestone(live) {
  const ms = live?.milestones || [];
  const v = live?.participants || 0;
  let hit = null;
  ms.forEach((m, i) => { if (v >= (m.threshold ?? m.target ?? 0)) hit = { ...m, index: i }; });
  return hit;
}

/* ------------------------------ tiny UI ------------------------------ */
function LivePill({ isFinalWindow, light }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 font-mono-ds text-[10px] uppercase tracking-[0.24em] border whitespace-nowrap ${
        light
          ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]"
          : "border-[#FF6A00] bg-[#FF6A00]/20 text-[#FF6A00] backdrop-blur"
      }`}
      data-testid="live-pill"
    >
      <span className="w-1.5 h-1.5 bg-[#FF6A00] rounded-full pulse-dot" />
      {isFinalWindow ? "Final" : "Live"}
    </span>
  );
}

function TimerChip({ to, label, accent, light, onComplete }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const diff = Math.max(0, new Date(to).getTime() - now);
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  useEffect(() => { if (diff <= 0 && onComplete) onComplete(); }, [diff, onComplete]);
  const c = accent ? "text-[#FF6A00]" : (light ? "text-[#161616]" : "text-white");
  return (
    <span
      className={`inline-flex items-baseline gap-1.5 px-2 py-1 font-mono-ds text-[10px] uppercase tracking-[0.2em] border whitespace-nowrap ${
        light ? "border-[#E5E1D8] bg-[#FFFFFF]" : "border-white/20 bg-black/50 backdrop-blur text-white"
      }`}
      data-testid={`timer-chip-${label.toLowerCase()}`}
    >
      <span className={light ? "text-[#6E6E6E]" : "text-white/60"}>{label}</span>
      <span className={`font-display text-sm tabular-nums ${c}`}>{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}</span>
    </span>
  );
}

function ViewerChip({ n, light }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 font-mono-ds text-[10px] uppercase tracking-[0.2em] border whitespace-nowrap ${
        light ? "border-[#E5E1D8] bg-[#FFFFFF] text-[#161616]" : "border-white/20 bg-black/50 backdrop-blur text-white"
      }`}
      data-testid="viewer-chip"
    >
      <span className={light ? "text-[#6E6E6E]" : "text-white/60"}>Viewers</span>
      <span className="font-display text-sm tabular-nums">{n ?? 0}</span>
    </span>
  );
}

function MilestoneChip({ milestone, large }) {
  const label = `T${(milestone.index ?? 0) + 1}`;
  return (
    <span
      className={`inline-flex items-baseline gap-2 font-mono-ds ${large ? "text-[11px] px-3 py-2" : "text-[10px] px-2 py-1"} uppercase tracking-[0.2em] border border-[#FF6A00] bg-[#FFFFFF] text-[#161616] shadow-[2px_2px_0_#264733] pointer-events-auto`}
      data-testid="milestone-chip"
    >
      <span className="text-[#FF6A00] font-bold">{label}</span>
      <span className="text-[#6E6E6E]">Unlocked at {milestone.threshold ?? milestone.target}+ viewers</span>
      <span className={`font-display ${large ? "text-lg" : "text-sm"} text-[#264733]`}>₹{milestone.price}</span>
    </span>
  );
}

function StockChip({ stock, large }) {
  const s = large ? "text-[11px] px-3 py-2" : "text-[10px] px-2 py-1";
  if (stock <= 0) {
    return <span className={`inline-flex items-center gap-1 font-mono-ds ${s} uppercase tracking-[0.2em] border border-[#FF6A00] bg-[#FFFFFF] text-[#FF6A00] font-bold pointer-events-auto`} data-testid="stock-chip">● Sold out</span>;
  }
  if (stock <= 10) {
    return <span className={`inline-flex items-center gap-1 font-mono-ds ${s} uppercase tracking-[0.2em] border border-[#FF6A00] bg-[#FFFFFF] text-[#FF6A00] pointer-events-auto`} data-testid="stock-chip">● Only <span className="font-bold ml-0.5">{stock}</span> left</span>;
  }
  return <span className={`inline-flex items-center gap-1 font-mono-ds ${s} uppercase tracking-[0.2em] border border-[#E5E1D8] bg-[#FFFFFF] text-[#6E6E6E] pointer-events-auto`} data-testid="stock-chip">● {stock} in stock</span>;
}

function PlaceholderScreen({ tag, title, sub, showCountdown, light }) {
  return (
    <div className={`w-full h-full flex items-center justify-center relative overflow-hidden ${light ? "bg-[#FFFFFF] border border-[#E5E1D8]" : "bg-[#0F0F0F]"}`}>
      <div className={`text-center relative px-6 ${light ? "text-[#161616]" : "text-white"}`}>
        <div className={`font-mono-ds text-[11px] uppercase tracking-[0.3em] mb-3 ${light ? "text-[#6E6E6E]" : "text-white/60"}`}>{tag}</div>
        <div className="font-display text-2xl md:text-4xl uppercase tracking-tight leading-tight">{title}</div>
        {showCountdown && <div className="mt-5 flex justify-center scale-90 origin-top"><Countdown to={showCountdown} /></div>}
        {sub && <div className={`mt-3 font-mono-ds text-[11px] uppercase tracking-widest ${light ? "text-[#6E6E6E]" : "text-white/60"}`}>{sub}</div>}
      </div>
    </div>
  );
}

/**
 * Compact scrollable info strip shown under the mobile buy CTA.
 * Was previously rendered as overlay chips on top of the video (milestone +
 * stock) which cluttered the live screen. Now bundles title · stock · 5-tier
 * milestone ladder into a horizontal snap-scroll row that lives out of the
 * way but is one swipe reveal for anyone who wants the detail.
 */
function LiveInfoStrip({ live, milestone, stock }) {
  const tiers = Array.isArray(live?.milestones) ? live.milestones : [];
  const currentIdx = milestone?.index;
  return (
    <div className="mt-2 -mx-3 px-3 flex gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-none" data-testid="live-info-strip">
      {/* Title chip */}
      <div className="shrink-0 snap-start min-w-[60%] border border-white/10 bg-black/40 px-3 py-2 font-mono-ds text-[10px] uppercase tracking-[0.20em] text-white/80" data-testid="info-strip-title">
        <div className="text-white/50 text-[9px] mb-0.5">// Drop</div>
        <div className="truncate">{live?.title || "Tonight's Drop"}</div>
        {live?.product?.name && <div className="text-white/60 text-[9px] truncate mt-0.5">{live.product.name}</div>}
      </div>
      {/* Stock chip */}
      <div className={`shrink-0 snap-start border px-3 py-2 font-mono-ds text-[10px] uppercase tracking-[0.20em] ${stock <= 0 ? "border-[#FF6A00] text-[#FF6A00]" : stock <= 10 ? "border-[#FF6A00] text-[#FF6A00]" : "border-white/10 text-white/80"} bg-black/40`} data-testid="info-strip-stock">
        <div className="text-white/50 text-[9px] mb-0.5">// Stock</div>
        <div className="tabular-nums">{stock <= 0 ? "Sold out" : `${stock} left`}</div>
      </div>
      {/* Tier chips */}
      {tiers.map((m, i) => {
        const hit = i <= (currentIdx ?? -1);
        return (
          <div
            key={i}
            className={`shrink-0 snap-start border px-3 py-2 font-mono-ds text-[10px] uppercase tracking-[0.20em] ${hit ? "border-[#B9E641] text-[#B9E641]" : "border-white/10 text-white/60"} bg-black/40`}
            data-testid={`info-strip-tier-${i + 1}`}
          >
            <div className="text-white/50 text-[9px] mb-0.5">// Tier {i + 1}</div>
            <div className="tabular-nums text-white">₹{Number(m.price).toLocaleString("en-IN")}</div>
            <div className="text-white/50 text-[9px] mt-0.5">≥{m.threshold ?? m.target}</div>
          </div>
        );
      })}
    </div>
  );
}


/**
 * Anonymous live-gate: shown when a visitor without an account lands on a live
 * that has already started (or ended). We don't stream video to anonymous
 * eyeballs — instead we invite them to sign in so they can actually buy the
 * drop or catch the next one.
 */
function AnonymousLiveGate({ live, status }) {
  const nav = useNavigate();
  const product = live?.product || {};
  const isLive = status === "live";
  const isFinalWindow = status === "final_window";
  const isEnded = status === "ended";
  const signIn = () => {
    try { track("anon_live_gate_signin", { live_id: live?.live_id, status }); } catch (_e) { /* noop */ }
    const next = encodeURIComponent(`/live/${live.live_id}`);
    nav(`/login?next=${next}`);
  };
  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#161616]" data-testid="anon-live-gate">
      <div className="px-4 md:px-10 py-4 border-b border-[#E5E1D8] flex items-center justify-between">
        <Link to="/" className="font-mono-ds text-xs uppercase tracking-[0.3em] text-[#6E6E6E] hover:text-[#FF6A00]" data-testid="anon-gate-back">← Back</Link>
        <div className="brand-tiny text-[#6E6E6E]">Drop #{String(live?.drop_no || 1).padStart(3, "0")}{live?.category ? ` · ${live.category}` : ""}</div>
      </div>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <div className="brand-tag mb-4 inline-block">
          {isLive && "// Live right now"}
          {isFinalWindow && "// Final window"}
          {isEnded && "// This drop ended"}
        </div>
        <h1 className="font-display uppercase leading-[0.95] tracking-tight text-4xl md:text-6xl">
          {isEnded ? (<>Missed it. <span className="text-outline">Catch the next one.</span></>) : (<>Sign in to <span className="text-outline">join the drop.</span></>)}
        </h1>
        <p className="mt-6 max-w-xl mx-auto text-[#6E6E6E] text-base md:text-lg leading-relaxed">
          {isEnded
            ? "This drop is in the archive. Sign in to see the next Season One drop and lock in your Founding Member spot."
            : "One product a night · 9 PM IST · 2-minute live. Sign in with Google to watch the room, chat with the host, and lock in the milestone-crashed price."}
        </p>
        {product.name && (
          <div className="mt-8 inline-flex items-center gap-3 border-2 border-[#161616] bg-[#FFFFFF] px-4 py-3 shadow-[3px_3px_0_#161616]" data-testid="anon-gate-product">
            {product.image_url && (
              <img src={product.image_url} alt={product.name} className="w-14 h-14 object-cover border border-[#E5E1D8]" />
            )}
            <div className="text-left">
              <div className="brand-tiny text-[#6E6E6E]">Tonight&rsquo;s Drop</div>
              <div className="font-display text-lg uppercase tracking-tight">{product.name}</div>
            </div>
          </div>
        )}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={signIn} className="btn-urgent h-12 px-8" data-testid="anon-gate-signin">
            Sign in with Google →
          </button>
          <Link to="/" className="btn-outline-ns h-12 px-8 inline-flex items-center justify-center" data-testid="anon-gate-schedule">
            See Full Schedule
          </Link>
        </div>
        <div className="mt-10 font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E]">
          ◆ Founding Members lock a permanent number · Free to join
        </div>
      </section>
    </div>
  );
}
